import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { usePeriodDates } from '../../../hooks/usePeriodDates'
import { fetchQuotesSummary } from '../query-api'
import type { FetchInvoicesSummaryParams } from '../types/fetchInvoicesSummaryParams'
import type { QuotesSummaryData } from '../types/quotes/quotesSummaryData'

interface UseFetchQuotesSummaryProps {
  customerId?: string
  enabled?: boolean
  period?: string
  searchQuery?: string
}

interface UseFetchQuotesSummaryResponse {
  data?: QuotesSummaryData
  isError: boolean
  isLoading: boolean
}

export const useFetchQuotesSummary = ({
  customerId,
  enabled = true,
  period,
  searchQuery,
}: UseFetchQuotesSummaryProps = {}): UseFetchQuotesSummaryResponse => {
  const { isLoading: isLoadingPeriodDates, fromDate, toDate } = usePeriodDates(period)

  const queryParams: FetchInvoicesSummaryParams = useMemo(
    () => ({
      ...(customerId ? { customerId } : {}),
      ...(searchQuery ? { searchQuery } : {}),
      ...(fromDate && toDate ? { fromDate, toDate } : {}),
    }),
    [customerId, fromDate, searchQuery, toDate],
  )

  const queryKey: [QueryKeys, FetchInvoicesSummaryParams] = useMemo(
    () => [QueryKeys.QuotesSummary, queryParams],
    [queryParams],
  )

  const {
    data: quotesSummaryData,
    isError,
    isIdle,
    isLoading,
  } = useQuery(queryKey, () => fetchQuotesSummary(queryParams), {
    enabled: enabled && !isLoadingPeriodDates,
    keepPreviousData: true,
    staleTime: defaultStaleTime,
  })

  return {
    data: quotesSummaryData?.data?.summary,
    isError,
    isLoading: isIdle || isLoading,
  }
}
